import React, { useEffect, useState } from "react";
import { DeliveryData } from "../OrderHistory";
import logo from "../../../assets/main-logo.png";
import nwc from "../../../assets/nwc.jpg";
import { currentLang } from "../../../utility/index";
import { useTranslation } from "react-i18next";

// Convert images to base64 for print compatibility
const convertImageToBase64 = (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL("image/png");
      resolve(dataURL);
    };
    img.onerror = reject;
    img.src = imageUrl;
  });
};

interface DeliveryModalProps {
  data: DeliveryData | undefined;
}

const metadata = {
  date: new Date(Date.now()).toLocaleString(),
  logoSrc: logo,
  logoSrcnwc: nwc,
  title: "Delivery Report",
};

function convertToMonth(monthString: number | null): string {
    if (!monthString) {
      return "Invalid month";
    }

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const monthIndex = month;
    if (monthIndex >= 0 && monthIndex < 12) {
      return monthNames[monthIndex];
    } else {
      return "Invalid month";
    }
  }

const month = new Date(Date.now()).getMonth();
const year = new Date(Date.now()).getFullYear();

const ComponentToPrintDelivery: React.FC<DeliveryModalProps> = ({ data }) => {
  const { t } = useTranslation();
  const [isArabic, setIsArabic] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [logoBase64, setLogoBase64] = useState<string>("");
  const [nwcBase64, setNwcBase64] = useState<string>("");
  
  useEffect(() => {
    const lang = currentLang();
    if (lang === "Arabic") setIsArabic(true);
    else setIsArabic(false);
    
    // Convert images to base64 for better print compatibility
    const loadImagesAsBase64 = async () => {
      try {
        const [logoDataUrl, nwcDataUrl] = await Promise.all([
          convertImageToBase64(logo),
          convertImageToBase64(nwc)
        ]);
        
        setLogoBase64(logoDataUrl);
        setNwcBase64(nwcDataUrl);
        setImagesLoaded(true);
        console.log("Delivery images converted to base64 successfully");
      } catch (error) {
        console.error("Failed to convert delivery images to base64:", error);
        // Fallback to original images
        setImagesLoaded(true);
      }
    };
    
    loadImagesAsBase64();
  }, []);
  return (
    <div 
      id="print-content-delivery" 
      className={`hidden print:block print:w-full print:max-w-full print:overflow-visible ${isArabic ? "rtl text-right" : "ltr text-left"}`}
      dir={isArabic ? "rtl" : "ltr"}
      style={{
        printColorAdjust: 'exact',
        WebkitPrintColorAdjust: 'exact'
      }}
    >
      <style>
        {`
          @media print {
            img {
              print-color-adjust: exact !important;
              -webkit-print-color-adjust: exact !important;
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
            }
            #print-content-delivery img {
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
            }
            .print-logo {
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
              height: 64px !important;
              width: auto !important;
              max-width: none !important;
            }
            /* Chrome-specific fixes */
            @media screen and (-webkit-min-device-pixel-ratio: 0) {
              img {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .print-logo {
                height: 64px !important;
                width: auto !important;
              }
            }
          }
        `}
      </style>
      <div className="flex justify-between items-center mb-6">
        <div className="flex flex-col gap-2 text-8">
          <p>{t("orders.date")}: {metadata?.date || "-"}</p>
      
          <div className="flex item center space-x-2">
              <img 
                src={logoBase64 || logo} 
                alt="Logo" 
                className="h-16 print:block print:!visible print-logo" 
                style={{
                  printColorAdjust: 'exact',
                  WebkitPrintColorAdjust: 'exact',
                  display: 'block',
                  height: '64px',
                  width: 'auto'
                }}
                onLoad={(e) => {
                  // Ensure image is loaded before printing
                  const img = e.target as HTMLImageElement;
                  img.style.display = 'block';
                }}
                crossOrigin="anonymous"
              />
              <img
                src={nwcBase64 || nwc}
                alt="NWC Logo"
                className="h-16 self-start print:block print:!visible print-logo"
                style={{
                  printColorAdjust: 'exact',
                  WebkitPrintColorAdjust: 'exact',
                  display: 'block',
                  height: '64px',
                  width: 'auto'
                }}
                onLoad={(e) => {
                  // Ensure image is loaded before printing
                  const img = e.target as HTMLImageElement;
                  img.style.display = 'block';
                }}
                crossOrigin="anonymous"
              />
            </div>
   
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold">
            {t(metadata?.title || "Dynamic Report")}
          </h1>
        </div>
        <div className="max-w-[50px]">
          <p className="text-sm">
            <b>{t("finance.month")} : </b>
            {convertToMonth(month)}
          </p>
          <p className="text-sm">
            <b>{t("finance.year")}: </b>
            {year}
          </p>
        </div>
      </div>
                           <div className="bg-white rounded-lg shadow-xl w-full lg:w-[80%] p-6">
          <div className="grid grid-cols-2 gap-x-12 gap-y-4 text-gray-700 mt-4">
            {/* Left Column */}
            <div>
              <p className="text-left mb-4">
                <span className="font-medium">{t("orders.deliveryNo")}:</span>{" "}
                {data?.DeliveryNo || "N/A"}
              </p>
              <p className="text-left mb-4">
                <span className="font-medium">{t("orders.outletName")}:</span>{" "}
                {data?.OutletName || "N/A"}
              </p>
              <p className="text-left mb-4">
                <span className="font-medium">{t("orders.sfaOrderID")}:</span>{" "}
                {data?.SFAOrderID || "N/A"}
              </p>
            </div>
            
            {/* Right Column */}
            <div>
              <p className="text-left mb-4">
                <span className="font-medium">{t("orders.shipmentNo")}:</span>{" "}
                {data?.ShipmentNo || "N/A"}
              </p>
              <p className="text-left mb-4">
                <span className="font-medium">{t("orders.orderNo")}:</span>{" "}
                {data?.OrderNo || "N/A"}
              </p>
              <p className="text-left mb-4">
                <span className="font-medium">{t("orders.receiverName")}:</span>{" "}
                {data?.ReceiverName || "N/A"}
              </p>
              <p className="text-left mb-4">
                <span className="font-medium">{t("orders.customerID")}:</span>{" "}
                {data?.Customer || "N/A"}
              </p>
            </div>
          </div>
         
         {/* Address spans full width */}
         <div className="mt-4">
           <p className="text-left">
             <span className="font-medium">{t("orders.address")}:</span>{" "}
             {data?.Address || "N/A"}
           </p>
         </div>

        <div className="mt-6 border-t pt-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 text-left">{t("orders.items")}</h3>
          {data?.Items && data?.Items?.length > 0 ? (
            <div className="w-full">
              <table className="w-full border-collapse text-sm border border-gray-300" style={{ direction: 'ltr' }}>
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-300">
                    <th className={`text-left font-semibold text-gray-900 p-1 border-r border-gray-300 text-xs w-[12%] ${isArabic ? "text-right" : "text-left"}`}>
                      {t("orders.deliveryNo")}
                    </th>
                    <th className={`text-left font-semibold text-gray-900 p-1 border-r border-gray-300 text-xs w-[12%] ${isArabic ? "text-right" : "text-left"}`}>
                      {t("orders.itemCode")}
                    </th>
                    <th className={`text-left font-semibold text-gray-900 p-1 border-r border-gray-300 text-xs w-[12%] ${isArabic ? "text-right" : "text-left"}`}>
                      {t("orders.materialCode")}
                    </th>
                    <th className={`text-left font-semibold text-gray-900 p-1 border-r border-gray-300 text-xs w-[20%] ${isArabic ? "text-right" : "text-left"}`}>
                      {t("orders.materialEN")}
                    </th>
                    <th className={`text-left font-semibold text-gray-900 p-1 border-r border-gray-300 text-xs w-[20%] ${isArabic ? "text-right" : "text-left"}`}>
                      {t("orders.materialAR")}
                    </th>
                    <th className={`text-center font-semibold text-gray-900 p-1 border-r border-gray-300 text-xs w-[8%]`}>
                      {t("orders.quantity")}
                    </th>
                    <th className={`text-center font-semibold text-gray-900 p-1 border-r border-gray-300 text-xs w-[8%]`}>
                      {t("orders.remainingOrderQuantity")}
                    </th>
                    <th className={`text-center font-semibold text-gray-900 p-1 text-xs w-[8%]`}>
                      {t("orders.uom")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.Items.map((item, index) => (
                    <tr key={index} className="border-b border-gray-300 hover:bg-gray-50">
                      <td className="p-1 border-r border-gray-300 font-medium text-xs break-words">
                        {item.DeliveryNo || "N/A"}
                      </td>
                      <td className="p-1 border-r border-gray-300 text-xs break-words">
                        {item.ItemCode || "N/A"}
                      </td>
                      <td className="p-1 border-r border-gray-300 text-xs break-words">
                        {item.MaterialCode || "N/A"}
                      </td>
                      <td className="p-1 border-r border-gray-300 text-xs break-words leading-tight">
                        {item.MaterialTextEn || "N/A"}
                      </td>
                      <td className="p-1 border-r border-gray-300 text-xs break-words leading-tight">
                        {item.MaterialTextAr || "N/A"}
                      </td>
                      <td className="p-1 border-r border-gray-300 text-center font-semibold text-xs">
                        {item.Quantity || "0"}
                      </td>
                      <td className="p-1 border-r border-gray-300 text-center text-xs">
                        {item.pending_quantity || "N/A"}
                      </td>
                      <td className="p-1 text-center text-xs">
                        {item.UoMText || "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 mt-2 text-center py-4">No items available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComponentToPrintDelivery;
